import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSocket } from "../utils/socket";
import { motion } from "framer-motion";
import { FaCrown, FaGem, FaStar, FaCoins } from "react-icons/fa";
import { loadStripe } from "@stripe/stripe-js";
import PaymentForms from "./PaymentForms";

const UpgradeAccount = () => {
  const { fetchWithRetry, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [globalPaymentHistory, setGlobalPaymentHistory] = useState([]);
  const [localPaymentHistory, setLocalPaymentHistory] = useState([]);
  const [coinBalance, setCoinBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeTab, setActiveTab] = useState("vàng");
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentGateways, setPaymentGateways] = useState({
    stripe: { enabled: false },
    paypal_api: { enabled: false },
    paypal_me: { enabled: false },
    coinpayments: { enabled: false },
    coinbase: { enabled: false },
    nowpayments: { enabled: false },
    binancepay: { enabled: false },
  });
  const [localPaymentGateways, setLocalPaymentGateways] = useState({
    momo: { enabled: false },
    zalopay: { enabled: false },
    vnpay: { enabled: false },
    vietqr: { enabled: false },
  });
  const [stripePromise, setStripePromise] = useState(null);
  const [pendingTransactions, setPendingTransactions] = useState(0);

  const getPlanBenefits = (planName) => {
    switch (planName.toLowerCase()) {
      case "vàng":
        return ["Nhắn tin không giới hạn", "Quà tặng ảo"];
      case "bạch kim":
        return ["Nhắn tin không giới hạn", "Video chat", "Ưu tiên hiển thị hồ sơ"];
      case "kim cương":
        return ["Nhắn tin không giới hạn", "Video chat", "Ưu tiên hiển thị hồ sơ", "Giao tiếp không giới hạn", "Quà tặng ảo"];
      default:
        return [];
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName.toLowerCase()) {
      case "vàng":
        return <FaCrown className="text-yellow-500 w-12 h-12" />;
      case "bạch kim":
        return <FaStar className="text-gray-300 w-12 h-12" />;
      case "kim cương":
        return <FaGem className="text-blue-500 w-12 h-12" />;
      default:
        return <FaCrown className="text-gray-500 w-12 h-12" />;
    }
  };

  const getFeatureTooltip = (feature) => {
    switch (feature) {
      case "Nhắn tin không giới hạn":
        return "Gửi tin nhắn không giới hạn đến bất kỳ người dùng nào.";
      case "Video chat":
        return "Trò chuyện qua video với người dùng khác.";
      case "Ưu tiên hiển thị hồ sơ":
        return "Hồ sơ của bạn được ưu tiên hiển thị trong danh sách tìm kiếm.";
      case "Giao tiếp không giới hạn":
        return "Không giới hạn các hình thức giao tiếp, bao gồm tin nhắn, video, và quà tặng.";
      case "Quà tặng ảo":
        return "Gửi quà tặng ảo để thể hiện sự quan tâm đến người dùng khác.";
      default:
        return "";
    }
  };

  const fetchCoinBalance = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-balance" });
      setCoinBalance(res.data.coinBalance || 0);
    } catch (error) {
      console.error("Lỗi khi lấy số dư coin:", error);
      setError("Không thể lấy số dư coin. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchMembershipPlans = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/membership-plans" });
      const plansWithBenefits = res.data.map((plan) => ({
        ...plan,
        benefits: getPlanBenefits(plan.name),
      }));
      setPlans(plansWithBenefits);
    } catch (error) {
      console.error("Lỗi khi lấy danh sách gói VIP:", error);
      setError("Không thể lấy danh sách gói VIP. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchCurrentMembership = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/current-membership" });
      if (res.data && res.data.membership) {
        setCurrentMembership(res.data.membership);
      } else {
        setCurrentMembership(null);
      }
    } catch (error) {
      console.error("Lỗi khi lấy thông tin gói hiện tại:", error);
      setError("Không thể lấy thông tin gói hiện tại. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchData = useCallback(async () => {
    if (!user) {
      setError("Vui lòng đăng nhập để truy cập trang này!");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Lấy số dư coin
      await fetchCoinBalance();

      // Lấy danh sách gói VIP
      await fetchMembershipPlans();

      // Lấy thông tin gói hiện tại
      await fetchCurrentMembership();

      // Lấy lịch sử giao dịch quốc tế (Mua gói VIP)
      try {
        const globalPaymentRes = await fetchWithRetry({ method: "get", url: "/api/payments/membership-payment-history" });
        const filteredGlobalPayments = globalPaymentRes.data.payments.filter(
          (payment) => payment.planModel === "MembershipPlan"
        );
        setGlobalPaymentHistory(filteredGlobalPayments || []);
        // Đếm số giao dịch đang chờ xử lý
        const pendingGlobal = filteredGlobalPayments.filter(
          (payment) => payment.status === "pending" || payment.status === "pending_verification"
        ).length;
        setPendingTransactions(pendingGlobal);
      } catch (error) {
        console.error("Lỗi khi lấy lịch sử giao dịch quốc tế:", error);
        setError("Không thể lấy lịch sử giao dịch quốc tế. Vui lòng thử lại sau!");
      }

      // Lấy lịch sử giao dịch nội địa (Mua gói VIP)
      try {
        const localPaymentRes = await fetchWithRetry({ method: "get", url: "/api/local-payments/local-membership-payment-history" });
        const filteredLocalPayments = localPaymentRes.data.payments.filter(
          (payment) => payment.planModel === "MembershipPlan"
        );
        setLocalPaymentHistory(filteredLocalPayments || []);
        // Đếm số giao dịch đang chờ xử lý
        const pendingLocal = filteredLocalPayments.filter(
          (payment) => payment.status === "pending" || payment.status === "pending_verification"
        ).length;
        setPendingTransactions((prev) => prev + pendingLocal);
      } catch (error) {
        console.error("Lỗi khi lấy lịch sử giao dịch nội địa:", error);
        setError("Không thể lấy lịch sử giao dịch nội địa. Vui lòng thử lại sau!");
      }

      // Lấy cấu hình cổng thanh toán
      try {
        const gatewaysRes = await fetchWithRetry({ method: "get", url: "/api/users/public-payment-gateways" });
        const gatewaysData = gatewaysRes.data;
        setPaymentGateways(gatewaysData);

        const localGatewaysRes = await fetchWithRetry({ method: "get", url: "/api/local-payments/public-local-payment-gateways" });
        const localGatewaysData = localGatewaysRes.data;
        setLocalPaymentGateways(localGatewaysData);

        if (!gatewaysData) {
          setError("Không nhận được dữ liệu cổng thanh toán từ server!");
        } else {
          if (
            (!gatewaysData.stripe || !gatewaysData.stripe.enabled) &&
            (!gatewaysData.paypal_api || !gatewaysData.paypal_api.enabled) &&
            (!gatewaysData.paypal_me || !gatewaysData.paypal_me.enabled) &&
            (!gatewaysData.coinpayments || !gatewaysData.coinpayments.enabled) &&
            (!gatewaysData.coinbase || !gatewaysData.coinbase.enabled) &&
            (!gatewaysData.nowpayments || !gatewaysData.nowpayments.enabled) &&
            (!gatewaysData.binancepay || !gatewaysData.binancepay.enabled) &&
            (!localGatewaysData.momo || !localGatewaysData.momo.enabled) &&
            (!localGatewaysData.zalopay || !localGatewaysData.zalopay.enabled) &&
            (!localGatewaysData.vnpay || !localGatewaysData.vnpay.enabled) &&
            (!localGatewaysData.vietqr || !localGatewaysData.vietqr.enabled)
          ) {
            setError("Không có cổng thanh toán nào được kích hoạt. Vui lòng liên hệ quản trị viên!");
          } else if (gatewaysData.paypal_me?.enabled && !gatewaysData.paypal_me.username) {
            setError("Cấu hình PayPal.me thiếu username. Vui lòng liên hệ quản trị viên!");
          } else if (gatewaysData.coinpayments?.enabled && !gatewaysData.coinpayments.merchantId) {
            setError("Cấu hình CoinPayments thiếu Merchant ID. Vui lòng liên hệ quản trị viên!");
          } else if (gatewaysData.stripe?.enabled && gatewaysData.stripe.publishableKey) {
            try {
              const stripe = await loadStripe(gatewaysData.stripe.publishableKey);
              setStripePromise(stripe);
            } catch (err) {
              setError("Không thể khởi tạo Stripe. Vui lòng thử lại sau.");
              console.error("Lỗi khi khởi tạo Stripe:", err);
            }
          }
        }
      } catch (error) {
        console.error("Lỗi khi lấy cấu hình cổng thanh toán:", error);
        setError("Không thể lấy cấu hình cổng thanh toán. Vui lòng thử lại sau!");
      }
    } catch (error) {
      console.error("Lỗi khi lấy dữ liệu:", error);
      if (error.message.includes("500")) {
        setError("Lỗi server khi tải dữ liệu. Vui lòng thử lại sau!");
      } else if (error.message.includes("401")) {
        setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
      } else {
        setError(error.message || "Không thể tải dữ liệu! Vui lòng thử lại sau.");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry, user, fetchCoinBalance, fetchMembershipPlans, fetchCurrentMembership]);

  useEffect(() => {
    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (notification.type === "membership_upgrade") {
        setNotificationMessage("Nâng cấp tài khoản thành công!");
        fetchData();
        setTimeout(() => {
          setNotificationMessage(null);
          setSelectedPlan(null);
          setPaymentMethod(null);
          navigate(-1);
        }, 3000);
      } else if (notification.type === "payment_pending") {
        setNotificationMessage(notification.message || "Giao dịch của bạn đã được ghi nhận và đang chờ xác nhận!");
        setTimeout(() => setNotificationMessage(null), 3000);
      } else if (notification.type === "payment_failed") {
        setNotificationMessage(notification.message || "Giao dịch thất bại. Vui lòng thử lại!");
        setTimeout(() => setNotificationMessage(null), 3000);
      } else if (notification.type === "payment_refunded") {
        setNotificationMessage(notification.message || "Giao dịch của bạn đã được hoàn tiền!");
        fetchData();
        setTimeout(() => setNotificationMessage(null), 3000);
      }
    });

    socket.on("error", (error) => {
      setError(error.message || "Lỗi kết nối WebSocket!");
    });

    return () => {
      socket.off("notification");
      socket.off("error");
    };
  }, [navigate, fetchData]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    fetchData();
  }, [user, navigate, fetchData, authLoading]);

  const handleRenewPlan = () => {
    if (currentMembership && currentMembership.plan) {
      const currentPlan = plans.find((plan) => plan._id === currentMembership.plan._id);
      if (currentPlan) {
        setSelectedPlan(currentPlan);
        document.getElementById("payment-method-section")?.scrollIntoView({ behavior: "smooth" });
      } else {
        setError("Không tìm thấy gói hiện tại trong danh sách gói!");
      }
    }
  };

  const handleSelectPlanFromCompare = (planName) => {
    const plan = plans.find((p) => p.name.toLowerCase() === planName.toLowerCase());
    if (plan) {
      setSelectedPlan(plan);
      setShowCompareModal(false);
      setActiveTab(planName.toLowerCase());
      setNotificationMessage(`Đã chọn gói ${plan.name}!`);
      setTimeout(() => setNotificationMessage(null), 3000);
      document.getElementById("payment-method-section")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleUpgradeWithCoin = async (plan) => {
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/users/upgrade-membership-with-coin",
        data: { planId: plan._id },
      });
      setNotificationMessage(response.data.message || "Nâng cấp gói bằng coin thành công!");
      setCoinBalance(response.data.coinBalance);
      fetchData();
      setTimeout(() => {
        setNotificationMessage(null);
        navigate(-1);
      }, 3000);
    } catch (error) {
      console.error("Lỗi khi nâng cấp gói bằng coin:", error);
      setError(error.response?.data?.message || "Lỗi khi nâng cấp gói bằng coin!");
    }
  };

  const handlePaymentSuccess = useCallback(() => {
    setNotificationMessage("Nâng cấp tài khoản thành công!");
    fetchData();
    setTimeout(() => {
      setNotificationMessage(null);
      setSelectedPlan(null);
      setPaymentMethod(null);
      navigate(-1);
    }, 3000);
  }, [navigate, fetchData]);

  const handlePaymentPending = useCallback((data) => {
    const message = data && data.message ? data.message : "Giao dịch của bạn đã được ghi nhận và đang chờ xác nhận!";
    setNotificationMessage(message);
    setTimeout(() => setNotificationMessage(null), 3000);
  }, []);

  const isPlanExpiringSoon = () => {
    if (!currentMembership || !currentMembership.endDate) return false;
    const currentDate = new Date();
    const endDate = new Date(currentMembership.endDate);
    const daysUntilExpiry = Math.ceil((endDate - currentDate) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 || currentMembership.status === "expired";
  };

  const filteredPlans = plans.filter((plan) => {
    const planNameLower = plan.name.toLowerCase();
    if (activeTab === "vàng") return planNameLower === "vàng";
    if (activeTab === "bạch kim") return planNameLower === "bạch kim";
    if (activeTab === "kim cương") return planNameLower === "kim cương";
    return true;
  });

  if (loading || authLoading) {
    return <p className="text-center text-gray-500">Đang tải...</p>;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <h2 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">
        Nâng cấp tài khoản của bạn
      </h2>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          {error.includes("Phiên làm việc hết hạn") ? (
            <button
              onClick={() => navigate("/login")}
              className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
            >
              Đăng nhập lại
            </button>
          ) : (
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
            >
              Đóng
            </button>
          )}
        </div>
      )}

      {notificationMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{notificationMessage}</div>
      )}

      {/* Thông báo nếu có giao dịch đang chờ xử lý */}
      {pendingTransactions > 0 && (
        <div className="mb-4 p-4 bg-yellow-100 text-yellow-700 rounded-lg">
          <p>Bạn hiện có {pendingTransactions} giao dịch đang chờ xử lý. Bạn vẫn có thể tạo giao dịch mới nếu cần.</p>
        </div>
      )}

      {/* Gói hiện tại */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Gói hiện tại của bạn</h3>
        {currentMembership ? (
          <div className="p-6 bg-white rounded-xl shadow-lg">
            <p className="text-gray-700"><strong>Gói:</strong> {currentMembership.plan?.name || "Không xác định"}</p>
            <p className="text-gray-700"><strong>Ngày bắt đầu:</strong> {new Date(currentMembership.startDate).toLocaleDateString()}</p>
            <p className="text-gray-700"><strong>Ngày hết hạn:</strong> {new Date(currentMembership.endDate).toLocaleDateString()}</p>
            <p className="text-gray-700"><strong>Trạng thái:</strong> {currentMembership.status === "active" ? "Đang hoạt động" : "Hết hạn"}</p>
            {isPlanExpiringSoon() && (
              <button
                onClick={handleRenewPlan}
                className="mt-4 px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition duration-200"
              >
                Gia hạn gói
              </button>
            )}
          </div>
        ) : (
          <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">Bạn chưa đăng ký gói nào.</p>
        )}
      </div>

      {/* Số dư coin */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Số dư Coin của bạn</h3>
        <div className="p-6 bg-white rounded-xl shadow-lg flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <FaCoins className="text-yellow-500 w-8 h-8" />
            <p className="text-gray-700 text-lg"><strong>Số dư:</strong> {coinBalance} Coin</p>
          </div>
          <button
            onClick={() => navigate("/coin-purchase")}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
          >
            Mua Coin
          </button>
        </div>
      </div>

      {/* Danh sách gói nâng cấp */}
      <div className="mb-8">
        <div className="flex space-x-6 border-b border-gray-200 mb-6">
          {["vàng", "bạch kim", "kim cương"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium ${activeTab === tab ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-500"}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
          <button
            onClick={() => setShowCompareModal(true)}
            className="ml-4 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition duration-200"
          >
            So sánh gói
          </button>
        </div>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {filteredPlans.map((plan) => (
            <div
              key={plan._id}
              className={`p-6 bg-white rounded-xl shadow-lg cursor-pointer ${selectedPlan?._id === plan._id ? "border-2 border-blue-500" : ""}`}
              onClick={() => setSelectedPlan(plan)}
            >
              <div className="flex justify-center mb-4">{getPlanIcon(plan.name)}</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">{plan.name}</h3>
              <p className="text-gray-600 mb-2 text-center">{plan.duration} Tháng</p>
              <p className="text-2xl font-bold text-gray-800 mb-2 text-center">{plan.price.toLocaleString()} VND/tháng</p>
              <p className="text-lg text-gray-600 mb-2 text-center">Tổng: {plan.totalPrice.toLocaleString()} VND</p>
              {plan.coinPrice && (
                <p className="text-lg text-gray-600 mb-2 text-center flex items-center justify-center">
                  <FaCoins className="mr-2 text-yellow-500" /> {plan.coinPrice} Coin
                </p>
              )}
              <p className="text-green-600 font-medium mb-4 text-center">Tiết kiệm {plan.discount}%</p>
              <ul className="text-sm text-gray-700 space-y-2 mb-4 text-center">
                {plan.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center justify-center">
                    <span className="text-green-500 mr-2">•</span> {benefit}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col space-y-2">
                <button
                  onClick={() => setSelectedPlan(plan)}
                  className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition duration-200"
                >
                  Nâng cấp bằng tiền
                </button>
                {plan.coinPrice && (
                  <button
                    onClick={() => handleUpgradeWithCoin(plan)}
                    className={`w-full py-2 rounded-lg flex items-center justify-center ${
                      coinBalance >= plan.coinPrice
                        ? "bg-yellow-500 text-white hover:bg-yellow-600"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    } transition duration-200`}
                    disabled={coinBalance < plan.coinPrice}
                  >
                    <FaCoins className="mr-2" /> Nâng cấp bằng {plan.coinPrice} Coin
                  </button>
                )}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Thanh toán nâng cấp tài khoản */}
      {selectedPlan && (
        <div id="payment-method-section" className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Thanh toán gói {selectedPlan.name}
          </h3>
          <PaymentForms
            plan={selectedPlan}
            packageId={null}
            isCoinPurchase={false}
            onSuccess={handlePaymentSuccess}
            onPending={handlePaymentPending}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            paymentGateways={paymentGateways}
            localPaymentGateways={localPaymentGateways}
            fetchWithRetry={fetchWithRetry}
            user={user}
            stripePromise={stripePromise}
          />
        </div>
      )}

      {/* Lịch sử thanh toán Mua Gói VIP */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Lịch sử thanh toán mua gói VIP</h3>
        <div className="mb-8">
          <h4 className="text-md font-medium text-gray-700 mb-2">Giao dịch quốc tế</h4>
          {globalPaymentHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg shadow-lg">
                <thead>
                  <tr className="bg-blue-500 text-white">
                    <th className="p-4 text-left">Gói</th>
                    <th className="p-4 text-left">Số tiền (VND)</th>
                    <th className="p-4 text-left">Phương thức</th>
                    <th className="p-4 text-left">Trạng thái</th>
                    <th className="p-4 text-left">Ngày giao dịch</th>
                  </tr>
                </thead>
                <tbody>
                  {globalPaymentHistory.map((payment) => (
                    <tr key={payment._id} className="border-b hover:bg-gray-50 transition duration-200">
                      <td className="p-4 text-gray-700">{payment.planId?.name || "N/A"}</td>
                      <td className="p-4 text-gray-700">{payment.amount.toLocaleString()}</td>
                      <td className="p-4 text-gray-700">
                        {payment.paymentMethod === "stripe"
                          ? "Stripe"
                          : payment.paymentMethod === "paypal_api"
                          ? "PayPal API"
                          : payment.paymentMethod === "paypal_me"
                          ? "PayPal.me"
                          : payment.paymentMethod === "coinpayments"
                          ? "CoinPayments (Tiền điện tử)"
                          : payment.paymentMethod === "coinbase"
                          ? "Coinbase Commerce (Tiền điện tử)"
                          : payment.paymentMethod === "nowpayments"
                          ? "NOWPayments (Tiền điện tử)"
                          : payment.paymentMethod === "binancepay"
                          ? "Binance Pay (Tiền điện tử)"
                          : payment.paymentMethod}
                      </td>
                      <td className="p-4 text-gray-700">
                        {payment.status === "completed"
                          ? "Hoàn tất"
                          : payment.status === "pending"
                          ? "Đang chờ"
                          : payment.status === "pending_verification"
                          ? "Đang chờ xác minh"
                          : payment.status === "refunded"
                          ? "Đã hoàn tiền"
                          : "Thất bại"}
                      </td>
                      <td className="p-4 text-gray-700">{new Date(payment.transactionDate).toLocaleString("vi-VN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">
              Bạn chưa có giao dịch quốc tế nào cho mua gói VIP.
            </p>
          )}
        </div>

        <div>
          <h4 className="text-md font-medium text-gray-700 mb-2">Giao dịch nội địa</h4>
          {localPaymentHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-lg shadow-lg">
                <thead>
                  <tr className="bg-blue-500 text-white">
                    <th className="p-4 text-left">Gói</th>
                    <th className="p-4 text-left">Số tiền (VND)</th>
                    <th className="p-4 text-left">Phương thức</th>
                    <th className="p-4 text-left">Trạng thái</th>
                    <th className="p-4 text-left">Ngày giao dịch</th>
                  </tr>
                </thead>
                <tbody>
                  {localPaymentHistory.map((payment) => (
                    <tr key={payment._id} className="border-b hover:bg-gray-50 transition duration-200">
                      <td className="p-4 text-gray-700">{payment.planId?.name || "N/A"}</td>
                      <td className="p-4 text-gray-700">{payment.amount.toLocaleString()}</td>
                      <td className="p-4 text-gray-700">
                        {payment.paymentMethod === "momo"
                          ? "MoMo"
                          : payment.paymentMethod === "zalopay"
                          ? "ZaloPay"
                          : payment.paymentMethod === "vnpay"
                          ? "VNPay"
                          : payment.paymentMethod === "vietqr"
                          ? "VietQR"
                          : payment.paymentMethod}
                      </td>
                      <td className="p-4 text-gray-700">
                        {payment.status === "completed"
                          ? "Hoàn tất"
                          : payment.status === "pending"
                          ? "Đang chờ"
                          : payment.status === "pending_verification"
                          ? "Đang chờ xác minh"
                          : payment.status === "refunded"
                          ? "Đã hoàn tiền"
                          : "Thất bại"}
                      </td>
                      <td className="p-4 text-gray-700">{new Date(payment.transactionDate).toLocaleString("vi-VN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">
              Bạn chưa có giao dịch nội địa nào cho mua gói VIP.
            </p>
          )}
        </div>
      </div>

      {/* Modal so sánh gói */}
      {showCompareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-2xl w-full max-w-4xl">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">So sánh các gói</h3>
            <button
              onClick={() => setShowCompareModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition duration-200"
            >
              ×
            </button>
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead>
                <tr className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                  <th className="p-4 text-center">Tính năng</th>
                  <th className="p-4 text-center">VIP Vàng</th>
                  <th className="p-4 text-center">VIP Bạch Kim</th>
                  <th className="p-4 text-center">VIP Kim Cương</th>
                </tr>
              </thead>
              <tbody>
                {[
                  "Nhắn tin không giới hạn",
                  "Video chat",
                  "Ưu tiên hiển thị hồ sơ",
                  "Giao tiếp không giới hạn",
                  "Quà tặng ảo",
                ].map((feature) => (
                  <tr key={feature} className="border-b hover:bg-gray-50 transition duration-200">
                    <td className="p-4 text-gray-700 relative group">
                      {feature}
                      <span className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2">
                        {getFeatureTooltip(feature)}
                      </span>
                    </td>
                    <td className="p-4 text-center">{["Nhắn tin không giới hạn", "Quà tặng ảo"].includes(feature) ? "✔" : "✖"}</td>
                    <td className="p-4 text-center">{["Nhắn tin không giới hạn", "Video chat", "Ưu tiên hiển thị hồ sơ"].includes(feature) ? "✔" : "✖"}</td>
                    <td className="p-4 text-center">{["Nhắn tin không giới hạn", "Video chat", "Ưu tiên hiển thị hồ sơ", "Giao tiếp không giới hạn", "Quà tặng ảo"].includes(feature) ? "✔" : "✖"}</td>
                  </tr>
                ))}
                <tr>
                  <td className="p-4 text-gray-700"></td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => handleSelectPlanFromCompare("vàng")}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
                    >
                      Chọn ngay
                    </button>
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => handleSelectPlanFromCompare("bạch kim")}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
                    >
                      Chọn ngay
                    </button>
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={() => handleSelectPlanFromCompare("kim cương")}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
                    >
                      Chọn ngay
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowCompareModal(false)}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeAccount;